import { DOCUMENT } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	inject,
	OnDestroy,
	OnInit,
	Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Keyboard, KeyboardStyle } from '@capacitor/keyboard';
import { filter } from 'rxjs/operators';

import { Theme } from '@reach/interfaces/settings/theme.enum';
import { UserPreferences } from '@reach/interfaces/user/preferences.interface';

import { ClientService, SettingsService } from '~app-client/core/services';
import { ClientPlatform } from '~app-client/core/types';

@Component({
	selector: 'home-theme-reactor',
	template: '',
	styles: [':host { display: none; } '],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
})
export class HomeThemeReactorComponent implements OnInit, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	private readonly client = inject(ClientService);
	private readonly settingsService = inject(SettingsService);
	private readonly renderer = inject(Renderer2);
	private readonly document = inject(DOCUMENT);
	private readonly cdRef = inject(ChangeDetectorRef);

	private readonly rootElement = this.document.documentElement;

	constructor() {
		this.cdRef.detach();
	}

	ngOnInit(): void {
		this.settingsService.settings$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((settings) => {
				this.handleSettings(settings);
			});

		this.setBrowserTheme();

		this.client.appFocused$
			.pipe(
				filter((active) => !!active),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(() => {
				this.setBrowserTheme();
			});
	}

	ngOnDestroy(): void {
		this.renderer.removeAttribute(this.rootElement, 'app-theme');
		this.renderer.removeAttribute(this.rootElement, 'client-theme');
	}

	private handleSettings(settings: UserPreferences): void {
		this.changeTheme(settings.general.theme);
	}

	private changeTheme(theme: Theme): void {
		this.renderer.setAttribute(this.rootElement, 'app-theme', theme.toLowerCase());

		// set the prefered browser theme
		const nonAutoTheme = theme === Theme.AUTO ? this.client.browserTheme : theme;
		const meta = this.document.head.querySelector('meta[name=theme-color]');
		const color = nonAutoTheme === Theme.LIGHT ? '#FFFFFF' : '#121213';
		this.renderer.setAttribute(meta, 'content', color);

		// set the keyboard theme
		if (this.client.platform === ClientPlatform.IOS) {
			const keyboardStyle =
				nonAutoTheme === Theme.DARK ? KeyboardStyle.Dark : KeyboardStyle.Light;
			Keyboard?.setStyle?.({ style: keyboardStyle });
		}
	}

	private setBrowserTheme(): void {
		const prefersDarkMode = this.client.browserTheme === Theme.DARK;
		const attributeValue = prefersDarkMode ? 'dark' : 'light';

		this.renderer.setAttribute(this.document.documentElement, 'client-theme', attributeValue);
	}
}
